import { Link } from '@inertiajs/react'
import { useEffect } from 'react'

import Layout from '../../components/Layout'
import { CheckIcon } from '../../components/Icons'
import { useTranslation } from '@/lib/i18n'
import { trackEvent } from '@/lib/track'
import { Button } from '@/components/ui/button'

interface OrderItem {
  id: number
  name: string
  quantity: number
  subtotal: number
  customizations: { label: string; value: string | null }[]
}

interface Payment {
  brand: string | null
  last4: string | null
}

interface Order {
  id: number
  number: string
  customerName: string
  customerEmail: string
  customerPhone: string | null
  shippingAddress: string
  total: number
  payment: Payment | null
  items: OrderItem[]
}

interface Props {
  order: Order
}

/**
 * Spec 10 · Template 06 — Confirmation.
 *
 * The one big pink check moment in the app, 28/700 sentence-case
 * heading, then the receipt as a bordered card with hairline rows.
 * The order exists in the DB at this point (status "pending");
 * payment is arranged off-site, so the copy sets that expectation.
 * Mount fires the final funnel stage.
 */
export default function CheckoutConfirmation({ order }: Props) {
  const { t } = useTranslation()

  useEffect(() => {
    trackEvent('purchase', {
      label: `Order ${order.number}`,
      meta: {
        order_id: order.id,
        order_number: order.number,
        total: order.total,
        items: order.items.length,
      },
      stage: 'purchased',
    })
  }, [order])

  const details = [
    { label: t('confirmation.name'), value: order.customerName },
    { label: t('confirmation.email'), value: order.customerEmail },
    order.customerPhone ? { label: t('confirmation.phone'), value: order.customerPhone } : null,
    { label: t('confirmation.ships_to'), value: order.shippingAddress },
  ].filter(Boolean) as { label: string; value: string }[]

  return (
    <Layout>
      <div className="mx-auto w-full max-w-[720px] px-8 pt-16 max-[559px]:px-4 max-[559px]:pt-10">
        <div className="border-b border-hairline pb-6 text-center">
          <span className="inline-flex size-14 items-center justify-center rounded-full border border-hairline bg-surface text-signal">
            <CheckIcon size={28} />
          </span>
          <h1 className="mt-4 text-[28px] font-bold tracking-[-0.02em] text-ink">
            {t('confirmation.heading', { number: order.number })}
          </h1>
          <div className="mt-1.5 text-[13px] text-ink-faint tabular-nums">{order.number}</div>
          <p className="mx-auto mt-2.5 max-w-[52ch] text-[15px] leading-[1.6] text-ink-muted">
            {t('confirmation.copy', { name: order.customerName })}
          </p>
        </div>

        <section className="mt-6 rounded-lg border border-hairline p-5">
          <div className="mb-3 text-[14px] font-medium text-ink">{t('confirmation.items')}</div>

          <div className="flex flex-col divide-y divide-hairline">
            {order.items.map((item) => {
              const spec = item.customizations
                .filter((entry) => entry.value)
                .map((entry) => `${entry.label} · ${entry.value}`)

              return (
                <div key={item.id} className="py-3 first:pt-0">
                  <div className="flex justify-between gap-3 text-[15px] font-semibold text-ink">
                    <span>
                      {item.name} · ×{item.quantity}
                    </span>
                    <span className="tabular-nums">${item.subtotal.toFixed(2)}</span>
                  </div>
                  {spec.length > 0 && (
                    <div className="mt-1 text-[13px] text-ink-faint">{spec.join(' · ')}</div>
                  )}
                </div>
              )
            })}
          </div>

          <div className="flex justify-between gap-3 border-t border-hairline pt-3 text-[17px] font-bold text-ink">
            <span>{t('confirmation.total')}</span>
            <span className="tabular-nums">${order.total.toFixed(2)}</span>
          </div>

          {order.payment?.last4 && (
            <div className="mt-2 flex justify-between gap-3 text-[14px] text-ink-muted">
              <span>{t('confirmation.paid_with')}</span>
              <span className="tabular-nums">
                {order.payment.brand} •••• {order.payment.last4}
              </span>
            </div>
          )}
        </section>

        {/* Label column sizes to its content so long translations
            can't run under the value column. */}
        <dl className="mt-4 grid grid-cols-[max-content_1fr] gap-x-6 rounded-lg border border-hairline p-5">
          {details.map((detail, i) => {
            const rule = i < details.length - 1 ? 'border-b border-hairline' : ''
            return (
              <div key={detail.label} className="contents">
                <dt className={`py-2.5 text-[13px] text-ink-faint ${rule}`}>{detail.label}</dt>
                <dd className={`whitespace-pre-wrap py-2.5 text-[14px] text-ink ${rule}`}>
                  {detail.value}
                </dd>
              </div>
            )
          })}
        </dl>

        <Button asChild variant="outline" className="mt-8 max-[559px]:h-13 max-[559px]:w-full">
          <Link href="/">{t('confirmation.keep_shopping')}</Link>
        </Button>
      </div>
    </Layout>
  )
}
